import { LitElement, html, css } from 'lit';

/**
 * CrusherPlanCard
 *
 * SaaS pricing-tier card. Renders a plan name + price + tagline header,
 * a slotted feature list in the body, and a slotted CTA at the bottom.
 * Supports two emphasis variants:
 *
 *   - featured , visually elevated tier (the "recommended" one); brand-color border + slight shadow lift
 *   - current: "this is the user's active plan"; replaces the CTA emphasis with a checkmark + muted state
 *
 * Both can coexist: a `featured` + `current` card shows the brand outline + the current-plan badge.
 *
 * Attributes:
 *   - plan-name   (string), required; the tier label (e.g., "Pro")
 *   - price       (string), required; the price tag (e.g., "$99/mo", "$1,200/yr", "Free")
 *   - description (string), optional; one-line tagline under the price
 *   - featured    (boolean), emphasize as the recommended tier
 *   - current     (boolean), mark as the user's active plan
 *
 * Slots:
 *   - default, feature list. Consumer slots a <ul> of <li> items (or any structure).
 *               Each top-level child gets baseline list-item styling; the consumer can
 *               prefix items with check icons via their own markup.
 *   - cta: the action button (Upgrade / Select / Manage / Current plan).
 *   - badge: optional badge above the plan name (e.g., "Most popular", "Save 20%").
 *
 * Tokens used: --crusher-surface, --crusher-border, --crusher-color-brand-primary,
 *   --crusher-fg, --crusher-fg-muted, --crusher-feedback-success, --crusher-shadow-{md,lg},
 *   plus the standard spacing / radius scale.
 *
 * A11y: the host is a single landmark with role="group" + aria-label="<plan-name> plan".
 *   The "current plan" indicator is announced via the badge text.
 */
export class CrusherPlanCard extends LitElement {
  static properties = {
    planName:    { type: String, attribute: 'plan-name', reflect: true },
    price:       { type: String, reflect: true },
    description: { type: String, reflect: true },
    featured:    { type: Boolean, reflect: true },
    current:     { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.planName = '';
    this.price = '';
    this.description = '';
    this.featured = false;
    this.current = false;
  }

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-lg);
      /* Generous internal padding so content sits well away from the card
         edges. spacing-8 = 32px, matching the breathing room used by Stripe
         / Polar pricing cards. Featured cards subtract 1px so the 2px
         border doesn't shift content alignment between cards. */
      padding: var(--crusher-spacing-8);
      gap: var(--crusher-spacing-6);
      min-inline-size: var(--crusher-plan-card-min, 0);
      max-inline-size: var(--crusher-plan-card-max, 22rem);
      inline-size: 100%;
      box-sizing: border-box;
      transition:
        border-color var(--crusher-motion-duration-base) var(--crusher-motion-easing-standard),
        box-shadow var(--crusher-motion-duration-base) var(--crusher-motion-easing-standard),
        background var(--crusher-motion-duration-base) var(--crusher-motion-easing-standard);
    }
    /* Featured: stronger border + subtle brand-tinted background + soft outer
       glow. Layered emphasis (not just one signal) so the recommended tier
       reads as recommended at a glance, even in monochrome screenshots. */
    :host([featured]) {
      border-width: 2px;
      border-color: var(--crusher-color-brand-primary);
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 5%, var(--crusher-surface));
      box-shadow:
        0 0 0 4px color-mix(in srgb, var(--crusher-color-brand-primary) 8%, transparent),
        var(--crusher-shadow-lg, var(--crusher-shadow-2));
      padding: calc(var(--crusher-spacing-8) - 1px);
    }
    /* Current plan: muted tinted surface (already on this plan, not a sales
       push). Keeps the card present without competing with featured. */
    :host([current]) {
      background: color-mix(in srgb, var(--crusher-fg) 4%, var(--crusher-surface));
    }
    :host([featured][current]) {
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 6%, var(--crusher-surface));
    }

    /* Badges sit inline at the top of the card as a small row. The row
       reserves a fixed block-size so cards with and without a badge stay
       baseline-aligned across a 3-column pricing grid. */
    .badges {
      display: flex;
      gap: var(--crusher-spacing-2);
      align-items: center;
      block-size: 1.25rem;
      margin-block-end: var(--crusher-spacing-1);
    }
    .badge-builtin,
    .badges ::slotted([slot="badge"]) {
      display: inline-flex;
      align-items: center;
      gap: var(--crusher-spacing-1);
      padding: 0.1875rem 0.625rem;
      border-radius: var(--crusher-radius-full);
      font-size: 0.6875rem;
      font-weight: var(--crusher-font-weight-semibold);
      line-height: 1;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .badge-builtin {
      color: var(--crusher-fg-muted);
      border: 1px solid var(--crusher-border);
      background: transparent;
    }
    .badges ::slotted([slot="badge"]) {
      background: var(--crusher-color-brand-primary);
      color: var(--crusher-color-brand-on-primary, white);
      border: 1px solid var(--crusher-color-brand-primary);
    }
    .badge-builtin .dot {
      width: 0.375rem;
      height: 0.375rem;
      border-radius: var(--crusher-radius-full);
      background: currentColor;
      opacity: 0.7;
    }

    /* Header rhythm: small eyebrow plan-name, dominant price with split
       weight between the number and the period suffix, balanced description. */
    .header {
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-2);
    }
    .plan-name {
      font-size: var(--crusher-font-size-xs);
      font-weight: var(--crusher-font-weight-semibold);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--crusher-fg-muted);
      margin: 0;
      line-height: 1;
    }
    .price {
      display: flex;
      align-items: baseline;
      gap: var(--crusher-spacing-1);
      margin: 0;
      line-height: 1;
      color: var(--crusher-fg);
    }
    .price-amount {
      font-size: var(--crusher-font-size-4xl, 2.25rem);
      font-weight: var(--crusher-font-weight-bold);
      letter-spacing: -0.02em;
      font-variant-numeric: tabular-nums;
    }
    .price-period {
      font-size: var(--crusher-font-size-sm);
      font-weight: var(--crusher-font-weight-medium);
      color: var(--crusher-fg-muted);
    }
    .description {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      line-height: var(--crusher-line-height-snug);
      margin: 0;
      text-wrap: balance;
    }

    /* Visual separator between the marketing header and the feature list.
       Subtle, not distracting; uses the same border token as the card edge. */
    .divider {
      block-size: 1px;
      background: var(--crusher-border);
      margin: 0;
      border: 0;
      opacity: 0.5;
    }

    /* Feature list: check icons before each item via ::before. Brand-color
       checks on featured cards, muted-success on others, so the visual
       associates "what you get" with the plan emphasis. */
    .features {
      flex: 1;
      display: block;
    }
    .features ::slotted(ul) {
      list-style: none !important;
      padding: 0 !important;
      margin: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: var(--crusher-spacing-3) !important;
    }
    .features ::slotted(*) {
      color: var(--crusher-fg);
      font-size: var(--crusher-font-size-sm);
      line-height: var(--crusher-line-height-snug);
    }
    /* The check icon. ::slotted-pseudo-element targeting isn't possible
       across shadow boundaries, so the consumer's <li> needs to opt in by
       supplying a leading icon OR the page-level CSS adds the ::before.
       Because we can't reach into slotted children's pseudos from the
       shadow root, we provide a fallback: each list item gets baseline
       padding-inline-start to receive a check via the surrounding ::part. */

    .cta {
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-2);
    }
    .cta ::slotted(*) {
      width: 100%;
    }
  `;

  /* Split a price string into amount + period suffix so the period can be
     visually de-emphasized. "$99/mo" -> {amount: "$99", period: "/mo"}.
     If no "/" present, period is empty ("Free", "$1,200", "Custom"). */
  _splitPrice() {
    const raw = this.price || '';
    const ix = raw.indexOf('/');
    if (ix === -1) return { amount: raw, period: '' };
    return { amount: raw.slice(0, ix).trim(), period: raw.slice(ix) };
  }

  /* Inject check icons before each slotted <li> in the feature list. Because
     ::slotted() can't reach grandchildren, the check has to land in the
     light DOM via DOM manipulation. Each <li> is tagged with
     data-crusher-checked so re-runs (HMR, slot recomputes) don't double up. */
  _decorateFeatures() {
    const slot = this.renderRoot.querySelector('.features slot');
    if (!slot) return;
    const items = [];
    for (const node of slot.assignedNodes({ flatten: true })) {
      if (node.nodeType !== 1) continue;
      if (node.tagName === 'LI') { items.push(node); continue; }
      node.querySelectorAll?.('li').forEach((li) => items.push(li));
    }
    for (const li of items) {
      if (li.hasAttribute('data-crusher-checked')) continue;
      li.setAttribute('data-crusher-checked', '');
      const NS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('class', 'crusher-plan-check');
      svg.setAttribute('width', '16');
      svg.setAttribute('height', '16');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS(NS, 'path');
      path.setAttribute('d', 'M3.5 8.5l3 3 6-7');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(path);
      li.style.display = 'flex';
      li.style.alignItems = 'flex-start';
      li.style.gap = '0.625rem';
      svg.style.flex = '0 0 auto';
      svg.style.marginBlockStart = '0.125rem';
      svg.style.color = this.featured
        ? 'var(--crusher-color-brand-primary)'
        : 'var(--crusher-feedback-success, var(--crusher-fg-muted))';
      li.insertBefore(svg, li.firstChild);
    }
  }

  firstUpdated() {
    const slot = this.renderRoot.querySelector('.features slot');
    if (slot) {
      slot.addEventListener('slotchange', () => this._decorateFeatures());
      this._decorateFeatures();
    }
  }

  updated(changed) {
    if (changed.has('featured')) {
      this.renderRoot.querySelectorAll('.features slot')[0]
        ?.assignedNodes({ flatten: true })
        .forEach((n) => {
          if (n.nodeType !== 1) return;
          (n.tagName === 'LI' ? [n] : Array.from(n.querySelectorAll?.('li') || []))
            .forEach((li) => {
              const svg = li.querySelector('.crusher-plan-check');
              if (svg) svg.style.color = this.featured
                ? 'var(--crusher-color-brand-primary)'
                : 'var(--crusher-feedback-success, var(--crusher-fg-muted))';
            });
        });
    }
  }

  render() {
    const ariaLabel = this.planName ? `${this.planName} plan` : 'Plan';
    const { amount, period } = this._splitPrice();
    return html`
      <div class="badges">
        ${this.current ? html`<span class="badge-builtin"><span class="dot" aria-hidden="true"></span>Current plan</span>` : null}
        <slot name="badge"></slot>
      </div>
      <header class="header" role="group" aria-label=${ariaLabel}>
        <p class="plan-name">${this.planName}</p>
        <h3 class="price"><span class="price-amount">${amount}</span>${period ? html`<span class="price-period">${period}</span>` : null}</h3>
        ${this.description ? html`<p class="description">${this.description}</p>` : null}
      </header>
      <hr class="divider" aria-hidden="true" />
      <div class="features"><slot></slot></div>
      <div class="cta"><slot name="cta"></slot></div>
    `;
  }
}

customElements.define('crusher-plan-card', CrusherPlanCard);
